// Въвеждането в реда на „Целия списък" — чисти функции без React. Отметнатият
// ред носи поле „Количество" („Намерено" при инвентаризация), а при доставка и
// „Ед. цена" нето за базова единица + сумата на реда. Модалът само събира
// написаното; листът го прилага по собствените си правила (при доставка —
// както при ръчно въвеждане: количество, после цена, сумата се извежда).
// Покрито от catalogEntryModel.test.ts.
import { parseDecimal, roundMoney } from "../numbers";
import type { CatalogItem } from "./catalogPickerModel";

// qty — само количество (изписване, прехвърляне, инвентаризация);
// qtyPrice — количество + ед. цена + сумата (доставка)
export type CatalogEntryKind = "qty" | "qtyPrice";

export type CatalogEntryConfig = {
  kind: CatalogEntryKind;
  // заглавието на колоната с полето („Количество", „Намерено")
  qtyLabel: string;
  // „Намерено" приема и израз („10+5") — листът го смята при прилагане
  allowExpression?: boolean;
};

export type CatalogEntryField = "qty" | "price";

// Написаното в един ред. `price` липсва, докато операторът не пипне цената —
// тогава листът ползва своята (на реда или последната доставна).
export type CatalogEntryValue = { qty: string; price?: string };
// по id на реда, само за добавяните редове с въведено
export type CatalogEntryValues = Map<number, CatalogEntryValue>;

// Числовите полета приемат само цифри, запетая и точка (+ „+"/„−" за израз)
export const sanitizeEntryInput = (raw: string, allowExpression = false): string =>
  raw.replace(allowExpression ? /[^0-9.,+-]/g : /[^0-9.,]/g, "");

// Има ли в реда нещо за прилагане (празното количество без пипната цена — не)
export const hasEnteredValue = (value: CatalogEntryValue | undefined): value is CatalogEntryValue =>
  !!value && (value.qty.trim() !== "" || value.price !== undefined);

// Новото състояние след писане в поле (Map-ът е неизменяем за React)
export const withEntryField = (
  entries: CatalogEntryValues,
  id: number,
  field: CatalogEntryField,
  value: string,
): CatalogEntryValues => {
  const next = new Map(entries);
  const current = next.get(id) ?? { qty: "" };
  next.set(id, field === "qty" ? { ...current, qty: value } : { ...current, price: value });
  return next;
};

// Стойностите, които отиват към листа: само добавяните редове с въведено.
// Количеството се подава подрязано; празното остава "" (редът е празен).
export const enteredValuesOf = (ids: number[], entries: CatalogEntryValues): CatalogEntryValues => {
  const values: CatalogEntryValues = new Map();
  for (const id of ids) {
    const value = entries.get(id);
    if (!hasEnteredValue(value)) continue;
    values.set(id, value.price === undefined ? { qty: value.qty.trim() } : { qty: value.qty.trim(), price: value.price.trim() });
  }
  return values;
};

// Кои вече попълнени редове от листа въведеното би презаписало — за тях
// модалът пита, преди да добави (празен ред в листа се попълва без въпрос).
export const overwriteConflictsOf = (
  values: CatalogEntryValues,
  byId: Map<number, CatalogItem>,
): CatalogItem[] =>
  Array.from(values.keys())
    .map((id) => byId.get(id))
    .filter((item): item is CatalogItem => !!item?.filledValue);

// Цената в полето: написаната, иначе предложената от листа
export const entryPriceOf = (item: CatalogItem, value: CatalogEntryValue | undefined): string =>
  value?.price ?? item.suggestedPrice ?? "";

// Сумата нето на реда: к-во × базови единици в мярката × ед. цена, до
// стотинка (както листът). null — няма количество или цена.
export const entryLineSumOf = (item: CatalogItem, value: CatalogEntryValue | undefined): number | null => {
  const price = entryPriceOf(item, value);
  if (!value || value.qty.trim() === "" || price.trim() === "") return null;
  return roundMoney(parseDecimal(value.qty) * (item.entryFactor ?? 1) * parseDecimal(price));
};

// Общата сума на отметнатите (долната лента при доставка)
export const pickedSumOf = (items: CatalogItem[], entries: CatalogEntryValues): number =>
  roundMoney(items.reduce((sum, item) => sum + (entryLineSumOf(item, entries.get(item.id)) ?? 0), 0));

// Id-тата на полетата: `{id на реда}-qty` / `-price` (фокус и aria)
export const catalogFieldId = (optionId: string, field: CatalogEntryField) => `${optionId}-${field}`;
